/** What the player needs of an `<audio>` element. */
export type Clip = { play(): Promise<void>; pause(): void; onEnded(f: (() => void) | null): void };

export type PlayerOptions = {
  count: number;
  load(i: number, slow: boolean, use: (wav: string) => void, failed: () => void): void;
  /** Null when the page's one voice is refused (a recording holds it). */
  makeClip(wav: string): Clip | null;
  onPlaying(i: number | null): void;
  onError(i: number): void;
};

/**
 * The passage read aloud sentence by sentence (listen & write design §3.1–2,
 * §4): one `speak` clip per sentence, each next one fetched while the
 * current one plays, so it starts the moment the last ends. That is the
 * gap §9 leaves to the builder: a fetched clip starts on `ended`, with no
 * synthesis between; a sentence still synthesising is waited for.
 *
 * Every start bumps `run`, and a clip whose run is over never plays, so a
 * slow synthesis for a sentence the learner has tapped past cannot talk over
 * the one they asked for.
 */
export class SentencePlayer {
  private run = 0;
  private current: Clip | null = null;
  // Assigned in the body: node's strip-only TS loader rejects parameter
  // properties (`sayItBack.ts`'s `Recorder` has the same note).
  private readonly o: PlayerOptions;

  constructor(o: PlayerOptions) {
    this.o = o;
  }

  /** From sentence `i` to the end, or `i` alone when `once` (a tap to replay). */
  playFrom(i: number, slow: boolean, once = false): void {
    // The old highlight goes with its clip, not when the new one arrives.
    if (this.halt()) this.o.onPlaying(null);
    const run = ++this.run;
    this.step(run, i, slow, once);
  }

  stop(): void {
    this.run++;
    this.halt();
    this.o.onPlaying(null);
  }

  private halt(): boolean {
    const c = this.current;
    this.current = null;
    if (!c) return false;
    c.onEnded(null);
    c.pause();
    return true;
  }

  private step(run: number, i: number, slow: boolean, once: boolean): void {
    this.o.load(
      i,
      slow,
      (wav) => {
        if (run !== this.run) return;
        const clip = this.o.makeClip(wav);
        if (!clip) return this.end(run);
        this.current = clip;
        clip.onEnded(() => {
          if (run !== this.run) return;
          this.current = null;
          if (!once && i + 1 < this.o.count) this.step(run, i + 1, slow, once);
          else this.end(run);
        });
        // Announced once the element is actually playing: a dictation play
        // is charged on this, so a clip the browser refuses costs none.
        clip.play().then(
          () => {
            if (run === this.run) this.o.onPlaying(i);
          },
          () => this.end(run),
        );
        if (!once && i + 1 < this.o.count)
          this.o.load(
            i + 1,
            slow,
            () => {},
            () => {},
          );
      },
      () => {
        if (run !== this.run) return;
        this.end(run);
        this.o.onError(i);
      },
    );
  }

  private end(run: number): void {
    if (run !== this.run) return;
    this.run++;
    this.current = null;
    this.o.onPlaying(null);
  }
}
